import { FC, useState, useEffect } from "react";
import { Checkbox } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import type { AdminHistory } from "../../../../Types/Admin/AdminHistoryType";
import HistoryActions from "./HistoryActions";
import { Trash2 } from "lucide-react";

type Props = {
  items: AdminHistory[];
  onView: (item: AdminHistory) => void;
  onEdit: (item: AdminHistory) => void;
  onDelete: (id: number) => void;
  onBulkDelete?: (ids: number[]) => void;
  onAddCover?: () => void;
  showBulkActions?: boolean;
  isLoading?: boolean;
};

const HistoryTable: FC<Props> = ({
  items,
  onView,
  onEdit,
  onDelete,
  onBulkDelete,
  onAddCover,
  showBulkActions = true,
  isLoading = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = () => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  useEffect(() => {
    setSelectedIds(new Set());
    setCurrentPage(1);
  }, [items]);

  const totalPages = Math.ceil(items.length / itemsPerPage);
  const startItem = items.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, items.length);

  const paginatedItems = items.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectAll = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    if (checked) {
      const allIds = paginatedItems
        .map((item) => item.id)
        .filter((id): id is number => id !== undefined);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectItem = (id: number | undefined, checked: boolean) => {
    if (!id) return;

    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    onBulkDelete?.(ids);
  };

  const allSelected =
    paginatedItems.length > 0 &&
    paginatedItems.every((item) => item.id && selectedIds.has(item.id));

  return (
    <div className="w-full">
      {/* Top Right Delete Icon */}
      {showBulkActions && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0}
            className={`transition-colors p-1 ${
              selectedIds.size > 0
                ? "text-red-600 hover:text-red-800 cursor-pointer"
                : "text-gray-300 cursor-not-allowed"
            }`}
            title="Delete Selected"
          >
            <Trash2 size={20} />
          </button>
        </div>
      )}

      <div className="overflow-x-auto relative">
        {(isLoading || isTransitioning) && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center min-h-[200px]">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium text-gray-600">Loading...</span>
            </div>
          </div>
        )}
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {showBulkActions && (
                <th className="px-4 py-3 text-left w-10">
                  <Checkbox
                    checked={allSelected}
                    onChange={handleSelectAll}
                    indeterminate={selectedIds.size > 0 && !allSelected}
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 whitespace-nowrap">
                Year
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 whitespace-nowrap">
                Title & Description
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 whitespace-nowrap">
                Image
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-700 whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100 font-body">
            {paginatedItems.length === 0 ? (
              <tr>
                <td
                  colSpan={showBulkActions ? 5 : 4}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No history entries found.
                </td>
              </tr>
            ) : (
              paginatedItems.map((it) => (
                <tr key={it.id} className="hover:bg-gray-50 transition-colors">
                  {showBulkActions && (
                    <td className="px-4 py-4 align-top w-10">
                      <Checkbox
                        checked={it.id ? selectedIds.has(it.id) : false}
                        onChange={(e: CheckboxChangeEvent) =>
                          handleSelectItem(it.id, e.target.checked)
                        }
                      />
                    </td>
                  )}
                  <td className="px-4 py-4 align-top text-gray-800">
                    {it.year}
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="font-medium text-gray-800 text-sm">{it.title}</div>
                    {it.description1 && (
                      <p className="text-sm text-gray-600 mt-1">
                        {it.description1.length > 80
                          ? it.description1.slice(0, 80) + "…"
                          : it.description1}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top">
                    {it.imageUrl ? (
                      <img
                        src={it.imageUrl}
                        alt={it.title}
                        className="w-20 h-14 object-cover rounded border"
                      />
                    ) : (
                      <span className="text-sm text-gray-400">No image</span>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top text-right whitespace-nowrap">
                    <HistoryActions
                      onView={() => onView(it)}
                      onEdit={() => onEdit(it)}
                      onDelete={() => onDelete(it.id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center mt-4 text-sm font-body">
        <div className="hidden md:flex text-gray-600">
          Showing {startItem} to {endItem} of {items.length} entries
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Show</span>
            <select
              className="p-2 border rounded bg-white hover:bg-gray-50 cursor-pointer outline-none focus:border-orange-400 transition-colors"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
                triggerTransition();
              }}
            >
              {[100, 500, 1000, 1500].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === 1 || isTransitioning}
              onClick={() => {
                setCurrentPage(1);
                triggerTransition();
              }}
              title="First Page"
            >
              &lt;&lt;
            </button>
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === 1 || isTransitioning}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                triggerTransition();
              }}
              title="Previous Page"
            >
              &lt;
            </button>
            <div className="flex items-center justify-center min-w-[40px] h-9 bg-gray-50 border rounded-md font-medium text-orange-600">
              {currentPage}
            </div>
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === totalPages || totalPages === 0 || isTransitioning}
              onClick={() => {
                setCurrentPage(currentPage + 1);
                triggerTransition();
              }}
              title="Next Page"
            >
              &gt;
            </button>
            <button
              className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
              disabled={currentPage === totalPages || totalPages === 0 || isTransitioning}
              onClick={() => {
                setCurrentPage(totalPages);
                triggerTransition();
              }}
              title="Last Page"
            >
              &gt;&gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoryTable;